import { useMeControllerGetUserTasks } from '@/api/generated/default/default';
import useQuestionnaireData from '@/hooks/use-questionnaire-data';
import useRiskFactorsSummary from '@/hooks/use-risk-factors-summary';
import useOnMount from '@/hooks/useOnMount';
import useGetMe from '@/queries/me.queries';
import { useGetQuestionnaire } from '@/queries/questionnaire.queries';
import { useTriggerPotentialRiskMutation } from '@/queries/risk-processing-status.queries';
import { useGetRisk } from '@/queries/risk.queries';
import { getRouteLoadingScreen } from '@/utils';
import { useRef } from 'react';

export default function useDashboardController() {
  const { data: meData, isLoading: isLoadingMe, refetch: refetchMe } = useGetMe();
  const { data: tasksData, isLoading: isLoadingTasks } = useMeControllerGetUserTasks();
  const { data: riskData, isLoading: isLoadingRisk } = useGetRisk();
  const name = meData?.profile?.name;

  const { data: userQuestionnaire } = useGetQuestionnaire();
  const { questionnaireStatus, assessmentProgress } = useQuestionnaireData(userQuestionnaire);
  const triggerRiskReprocessingMutation = useTriggerPotentialRiskMutation();
  const triggeredReprocessing = useRef<any>(null);

  const { risk, averageRiskPercentage, potentialRisk } = riskData ?? {
    risk: { overallRisk: 0 },
    averageRiskPercentage: 0,
    potentialRisk: 0,
  };

  useOnMount(() => {
    refetchMe();
  });

  const { riskFactorsSummary, modifiableRiskFactorCount } = useRiskFactorsSummary();

  const isLoading = isLoadingMe || isLoadingRisk || isLoadingTasks;

  if (!isLoading && potentialRisk === 0 && risk.overallRisk > 0 && !triggeredReprocessing.current) {
    triggeredReprocessing.current = true;
    triggerRiskReprocessingMutation.mutateAsync();
    window.location.href = getRouteLoadingScreen(window.location.pathname);
  }

  const chartSeries = [
    {
      data: [
        {
          x: 'Factors decreasing your risk',
          y: riskFactorsSummary.negativeImpactRiskFactorsCount,
        },
        {
          x: 'Factors increasing your risk',
          y: riskFactorsSummary.positiveImpactRiskFactorsCount,
        },
        {
          x: 'Factors you can modify',
          y: modifiableRiskFactorCount,
        },
      ],
    },
  ];

  return {
    name,
    questionnaireStatus,
    assessmentProgress,
    isLoading,
    currentRisk: risk.overallRisk,
    averageRisk: averageRiskPercentage,
    potentialRisk,
    tasks: tasksData?.data,
    averageRiskPercentage,
    chartSeries,
  };
}
